import React, { Fragment, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TimeDisplay from 'components/TimeDisplay';
import LoadAtStop from 'components/LoadAtStop';
import { EXTRAS } from 'utils/constants/jobDetails';
import Distance from 'components/Distance';
import StopStates from 'components/StopStates';
import Tag from 'components/Tag';
import DateFormat from 'components/DateFormat/DateFormat';
import SupportButton from 'components/SupportButton';
import cx from 'classnames';
import { capitalize } from 'utils/functions';

const SplittedComments = ({ comments }) => {
  return (comments || '').split('\n').map((text, key) => (
    <Fragment key={key}>
      {text}
      <br />
    </Fragment>
  ));
};

const StopComments = ({ comments }) => {
  const [showComments, setShowComments] = useState(false);
  const { t } = useTranslation('Jobs');
  if (!comments) return null;
  return (
    <div className="stops-details__comments">
      <span
        className={cx('stops-details__comments-text', {
          'stops-details__comments-text--show-more': showComments,
        })}
      >
        <SplittedComments comments={comments} />
      </span>
      {comments && comments.length >= 129 && (
        <>
          <button
            data-testid="stops-details__comments-button"
            className="stops-details__comments-button"
            onClick={() => setShowComments(!showComments)}
          >
            {!showComments && <span>{t('viewMore')}</span>}
            {showComments && <span>{t('viewLess')}</span>}
          </button>
          {showComments && (
            <div
              className="stops-details__comments-tooltip"
              data-testid="stops-details__comments-tooltip"
            >
              <SplittedComments comments={comments} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StopsDetails = ({
  className,
  expanded,
  showAdvanceButton,
  journey,
  refetchData,
  hideSupportButton,
}) => {
  const { t } = useTranslation();

  const renderHandlingTag = useCallback(
    (stop) => {
      if (
        stop.extras?.some(
          (extra) =>
            extra === EXTRAS.HANDLING_LOADING ||
            extra === EXTRAS.HANDLING_UNLOADING
        )
      ) {
        return (
          <div
            className="stops-details__handling"
            data-testid="stops-details__handling"
          >
            <Tag text={t('Offers:HANDLING')} skin="blue" />
          </div>
        );
      }
    },
    [t]
  );

  return (
    <div className={className} data-testid="stops-details">
      <div className="stops-details">
        <div className="stops-details__container">
          <div className="stops-details__header">
            <div className="stops-details__stops-container">
              {journey?.stops && (
                <span className="stops-details__stops">
                  {t('Offers:stopWithCount', { count: journey?.stops?.length })}
                </span>
              )}
              {journey?.distance !== undefined && (
                <span className="stops-details__distance">
                  <Distance distance={journey.distance} />
                </span>
              )}
            </div>
            {journey?.id && !hideSupportButton && (
              <div className="stops-details__actions-container">
                <SupportButton journeyId={journey.id} />
              </div>
            )}
          </div>
          <div className="stops-details__body">
            <ul className="stops-details__list">
              {journey?.stops.map((stop, index) => {
                return (
                  <li
                    className="stops-details__item"
                    key={`stop-${index}`}
                    data-testid="stops-details__item"
                  >
                    <div className="stops-details__body">
                      <div className="stops-details__block stops-details__block--big">
                        <div className="stops-details__block-title">
                          {stop.load_pickup?.length > 0
                            ? t('Jobs:origin')
                            : t('Jobs:destiny')}
                        </div>
                        <div className="stops-details__date-time">
                          <span className="stops-details__date">
                            <DateFormat
                              date={stop.time_from}
                              time_zone={stop.time_from_tz}
                            />
                          </span>
                          <span className="stops-details__separator">{`|`}</span>
                          <span className="stops-details__time">
                            <TimeDisplay
                              time_from={stop.time_from}
                              time_to={stop.time_to}
                              time_from_tz={stop.time_from_tz}
                              time_to_tz={stop.time_to_tz}
                            />
                          </span>
                        </div>
                        {expanded &&
                          stop.address?.latitude &&
                          stop.address?.longitude && (
                            <div data-testid="stops-details__expanded-address">
                              {stop.address.company_name && (
                                <div className="stops-details__company-name">
                                  {stop.address.company_name}
                                </div>
                              )}
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${stop.address.latitude},${stop.address.longitude}`}
                                className="stops-details__address-link"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {stop.address.address_line && (
                                  <div className="stops-details__address-line--link">
                                    {capitalize(stop.address.address_line)}
                                  </div>
                                )}
                                <div className="stops-details__address-line--link">
                                  {`${stop.address.zipcode} - ${capitalize(
                                    stop.address.label
                                  )} ${stop.address.country.toUpperCase()}`}
                                </div>
                              </a>
                            </div>
                          )}
                        {!expanded && (
                          <div className="stops-details__address-cell-address">
                            <div className="stops-details__address-line">{`${stop.label_to} ${stop.country}`}</div>
                          </div>
                        )}
                      </div>
                      <div className="stops-details__block stops-details__block--small">
                        <div className="stops-details__block-title">
                          {stop.load_pickup?.length > 0
                            ? t('Jobs:pickup')
                            : t('Jobs:dropoff')}
                        </div>
                        {!!stop.load_pickup && (
                          <LoadAtStop loads={stop.load_pickup} />
                        )}
                        {!!stop.load_dropoff && (
                          <LoadAtStop loads={stop.load_dropoff} />
                        )}
                        {renderHandlingTag(stop)}
                      </div>
                      <div className="stops-details__block stops-details__block--big">
                        {expanded && (
                          <>
                            <div className="stops-details__block-title">
                              {t('Jobs:details')}
                            </div>
                            {!stop.internal_reference &&
                              !stop.address.contact_name &&
                              !stop.address.contact_phone &&
                              !stop.comments && (
                                <div className="stops-details__no-details">
                                  {t('Jobs:noDetails')}
                                </div>
                              )}
                            {stop.internal_reference && (
                              <div className="stops-details__ref">
                                <>
                                  <span className="stops-details__ref-title">{`${t(
                                    'OfferDetail:internalRef'
                                  )}: `}</span>
                                  <span className="stops-details__ref-text">
                                    {stop.internal_reference}
                                  </span>
                                </>
                              </div>
                            )}
                            {(stop.address.contact_name ||
                              stop.address.contact_phone) && (
                              <div className="stops-details__company-contact">
                                <span className="stops-details__contact-item">
                                  {`${
                                    stop.address.contact_name
                                      ? stop.address.contact_name
                                      : ''
                                  } ${
                                    stop.address.contact_name &&
                                    stop.address.contact_phone
                                      ? '-'
                                      : ''
                                  } ${stop.address.contact_phone}`}
                                </span>
                              </div>
                            )}
                            <StopComments comments={stop.comments} />
                          </>
                        )}
                      </div>
                      {expanded && (
                        <div className="stops-details__block stops-details__block--dashed stops-details__block--small">
                          <StopStates
                            shipment_status={stop.shipment_status}
                            full_status={journey.full_status}
                            stop_id={index}
                            showAdvanceButton={showAdvanceButton}
                            is_late={stop.is_late}
                            refetchData={refetchData}
                            journeyStatus={stop.status}
                            journeyId={journey.id}
                          />
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

StopsDetails.defaultProps = {
  distance: null,
  expanded: false,
  withStatus: false,
  showAdvanceButton: false,
  refetchData: null,
  hideSupportButton: false,
};

StopsDetails.propTypes = {
  className: PropTypes.string.isRequired,
  expanded: PropTypes.bool,
  withStatus: PropTypes.bool,
  showAdvanceButton: PropTypes.bool,
  journey: PropTypes.object.isRequired,
  refetchData: PropTypes.func,
  hideSupportButton: PropTypes.bool,
};

export default StopsDetails;
